import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

/**
 * Polls the tenant PCF microservice (`:3141`) every 60s with the user's JWT
 * + tenant from localStorage. Aggregates four counters into synthetic
 * `NotificationItem`s that the shell's global notification bell renders
 * alongside `/notifications` from the shell backend.
 *
 * Kept as a hook (not a component) so there is exactly one bell in the
 * shell header — this one folds PCF signals into it.
 */

/* Resolve the PCF backend URL at build time. Must already include the
   trailing /pcf — every services/pcf controller queried below
   (review-queue, recalc-triggers, evidence-packs, requests) is registered
   under a `pcf/...` prefix, so PCF_BASE + '/review-queue' etc. only resolves
   correctly once /pcf is baked into PCF_BASE itself (see apps/shell/.env). */
const PCF_BASE =
  (import.meta.env.VITE_PCF_API_BASE as string | undefined) || '/api/pcf';
const REFRESH_MS = 60_000;

/** Shape matches shell Header's `NotificationItem` interface. */
export interface PcfNotification {
  id: string;
  type: string;
  title: string;
  message?: string;
  isRead: boolean;
  createdAt: string;
  link?: string;
  metadata?: Record<string, unknown> | null;
}

function getAuthHeaders(): Record<string, string> | null {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const h: Record<string, string> = { Authorization: `Bearer ${token}` };
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const u = JSON.parse(userStr);
      if (u?.tenantId) h['X-Tenant-Id'] = u.tenantId;
    } catch { /* ignore */ }
  }
  return h;
}

async function pcfCount(path: string): Promise<number> {
  const headers = getAuthHeaders();
  if (!headers) return 0;
  try {
    const res = await fetch(`${PCF_BASE}${path}`, { headers });
    if (!res.ok) return 0;
    const body = await res.json();
    const data = body?.data;
    return Array.isArray(data) ? data.length : 0;
  } catch { return 0; }
}

async function pcfInboundOpenCount(): Promise<number> {
  const headers = getAuthHeaders();
  if (!headers) return 0;
  try {
    const res = await fetch(`${PCF_BASE}/requests`, { headers });
    if (!res.ok) return 0;
    const body = await res.json();
    const rows: any[] = Array.isArray(body?.data) ? body.data : [];
    return rows.filter(
      (r) => r.direction === 'inbound' && (r.status === 'requested' || r.status === 'in_progress'),
    ).length;
  } catch { return 0; }
}

export interface UsePcfNotificationsResult {
  items: PcfNotification[];
  unreadCount: number;
  /** Acknowledge one counter at its current value. */
  markRead: (id: string) => void;
  /** Acknowledge every counter currently showing. */
  markAllRead: () => void;
}

/* ── Read state ────────────────────────────────────────────────────────────
   These notifications are derived, not stored: each poll recomputes them from
   live counts, so there is no row anywhere to flip to "read" — which is why
   "Mark all read" used to leave them sitting in the bell forever.

   What we remember instead is the COUNT the user last acknowledged, per
   counter, per tenant. "3 review items open" goes read when dismissed and
   stays read while it is still 3; when it becomes 4 there is genuinely
   something new to see, so it surfaces again. */

function tenantId(): string {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'anon';
    return String(JSON.parse(raw)?.tenantId ?? 'anon');
  } catch {
    return 'anon';
  }
}

function storageKey(): string {
  return `esg.pcfNotificationsRead.${tenantId()}`;
}

type AckedCounts = Record<string, number>;

function loadAcked(): AckedCounts {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as AckedCounts) : {};
  } catch {
    return {};
  }
}

function saveAcked(acked: AckedCounts): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(acked));
  } catch {
    /* quota / private mode — silent */
  }
}

export function usePcfNotifications(): UsePcfNotificationsResult {
  const enabled = typeof localStorage !== 'undefined' && !!localStorage.getItem('token');

  const reviewQ = useQuery({
    queryKey: ['shell', 'pcf', 'review-open'],
    queryFn: () => pcfCount('/review-queue?status=open'),
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS / 2,
    enabled,
  });
  const recalcQ = useQuery({
    queryKey: ['shell', 'pcf', 'recalc-open'],
    queryFn: () => pcfCount('/recalc-triggers?status=open'),
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS / 2,
    enabled,
  });
  const evidenceQ = useQuery({
    queryKey: ['shell', 'pcf', 'evidence-in_progress'],
    queryFn: () => pcfCount('/evidence-packs?status=in_progress'),
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS / 2,
    enabled,
  });
  const inboundQ = useQuery({
    queryKey: ['shell', 'pcf', 'requests-inbound-open'],
    queryFn: () => pcfInboundOpenCount(),
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS / 2,
    enabled,
  });

  const [acked, setAcked] = useState<AckedCounts>(loadAcked);

  const { items, counts } = useMemo(() => {
    const now = new Date().toISOString();
    const slots: Array<{ kind: string; count: number; title: string; message: string; link: string }> = [
      { kind: 'pcf_review',   count: reviewQ.data   ?? 0, title: 'PCF review items open',        message: 'AI proposals waiting for accept / edit / reject.',   link: '/pcf/review?status=open' },
      { kind: 'pcf_recalc',   count: recalcQ.data   ?? 0, title: 'PCF recalc alerts open',       message: 'Products crossed the ≥10% recalculation threshold.', link: '/pcf/recalc?status=open' },
      { kind: 'pcf_evidence', count: evidenceQ.data ?? 0, title: 'Evidence packs in progress',   message: 'Level 2 / 3 packs waiting on verifier sign-off.',   link: '/pcf/evidence?status=in_progress' },
      { kind: 'pcf_inbound',  count: inboundQ.data  ?? 0, title: 'Inbound PCF requests',         message: 'Customers requesting your product carbon footprint.', link: '/pcf/requests?direction=inbound' },
    ];

    const visible = slots.filter((s) => s.count > 0);

    const built: PcfNotification[] = visible.map((s) => ({
      id: s.kind,
      type: s.kind,
      title: `${s.count} ${s.title}`,
      message: s.message,
      isRead: acked[s.kind] === s.count,
      createdAt: now,
      link: s.link,
      metadata: { source: 'pcf', count: s.count },
    }));

    const byKind: Record<string, number> = {};
    for (const s of visible) byKind[s.kind] = s.count;

    return { items: built, counts: byKind };
  }, [reviewQ.data, recalcQ.data, evidenceQ.data, inboundQ.data, acked]);

  const unreadCount = useMemo(() => items.filter((i) => !i.isRead).length, [items]);

  const markRead = useCallback(
    (id: string) => {
      const count = counts[id];
      if (count === undefined) return;
      setAcked((prev) => {
        if (prev[id] === count) return prev;
        const next = { ...prev, [id]: count };
        saveAcked(next);
        return next;
      });
    },
    [counts],
  );

  const markAllRead = useCallback(() => {
    setAcked((prev) => {
      const next = { ...prev, ...counts };
      saveAcked(next);
      return next;
    });
  }, [counts]);

  return { items, unreadCount, markRead, markAllRead };
}
